import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { Download } from "lucide-react";
import { toast } from "sonner";

import { wireParamValue } from "components/NewAdvancedSearch/envelope";
import { formatError } from "helpers/formatError";
import TableExportService from "services/TableExport";
import { Button, buttonVariants } from "components/ui/button";
import { Checkbox } from "components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "components/ui/dialog";
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldLabel,
} from "components/ui/field";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import { SaveButton } from "components/ui/save-button";
import { Spinner } from "components/ui/spinner";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "components/ui/tooltip";

const FORMAT_OPTIONS = [
  { value: "xlsx", text: "Excel (.xlsx)" },
  { value: "csv", text: "CSV (.csv)" },
];

export function applyFilters(filters, queryFilters, filterParams) {
  if (!filters) return;
  filters.forEach(({ key, title, type, json }) => {
    // A filter already serialized in the wire envelope goes out untouched:
    // the export applies the very params the grid was filtered by.
    const wire = wireParamValue(filterParams[title]);
    if (wire !== null) {
      queryFilters[key] = wire;
      return;
    }
    if (type === "input") {
      if (!key.startsWith("search:custom:")) {
        queryFilters[key] = filterParams[title];
      }
    } else if (type === "select" || type === "ruvixxSelect") {
      if (filterParams[title] && filterParams[title].length) {
        const values = filterParams[title].map(item => item.value);
        queryFilters[key] = json ? JSON.stringify(values) : values;
      }
    } else if (type === "dateRange") {
      if (filterParams[title] && filterParams[title].from) {
        queryFilters[key + ":from"] =
          filterParams[title].from.format("YYYY-MM-DD");
      }
      if (filterParams[title] && filterParams[title].to) {
        queryFilters[key + ":to"] = filterParams[title].to.format("YYYY-MM-DD");
      }
    } else if (type === "checkbox" && filterParams[title] === "true") {
      queryFilters[key] = true;
    } else if (type === "range" && filterParams[title]) {
      queryFilters[key + ":type"] = filterParams[title].type;
      queryFilters[key + ":start"] = filterParams[title].start;
      queryFilters[key + ":end"] = filterParams[title].end;
    } else if (
      type === "ruvixxToggle" &&
      String(filterParams[title]) === "true"
    ) {
      queryFilters[key] = true;
    }
  });
}

function applyCustomFilters(customFilters, queryFilters) {
  if (!customFilters) return;
  if (customFilters.length) {
    for (let customFilter of customFilters) {
      queryFilters[customFilter.selectValue] = customFilter.inputValue;
    }
  }
}

function applyTagFilters(tagsFilters, queryFilters) {
  if (!tagsFilters) return;
  if (tagsFilters.length) {
    for (let tagFilter of tagsFilters) {
      if (tagFilter.values.length) {
        queryFilters[`search:tags:${tagFilter.uuid}`] = JSON.stringify({
          operationType: tagFilter.operationType,
          tags: tagFilter.values,
        });
      }
    }
  }
}

function applySearchQuery(searchQuery, queryFilters) {
  if (searchQuery) queryFilters.search = searchQuery;
}

function applySortParams(sorted, queryFilters) {
  if (!sorted) return;
  if (sorted[0]) {
    queryFilters.sort_by = sorted[0].id;
    queryFilters.sort_dir = sorted[0].desc ? "desc" : "asc";
  }
}

export function DownloadModal({
  exportTableName,
  customTableSlug,
  defaultFilterParams = {},
  visibleColumns,
  searchQuery,
  sorted,
  filters,
  filterParams,
  customFilters,
  tagsFilters,
  downloadParams,
  showDisabledTags,
  savedSearchName,
  downloadConfig = {},
  isDisabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [format, setFormat] = useState(customTableSlug ? "csv" : "xlsx");
  const [isOptionChecked, setIsOptionChecked] = useState(false);

  const exportOption = downloadConfig.option;
  const hasChoices = Boolean(customTableSlug || exportOption);

  const buildQueryFilters = (additionalParams = {}) => {
    let queryFilters = { ...defaultFilterParams };
    applyFilters(filters, queryFilters, filterParams);
    applyCustomFilters(customFilters, queryFilters);
    applyTagFilters(tagsFilters, queryFilters);
    applySearchQuery(searchQuery, queryFilters);
    applySortParams(sorted, queryFilters);

    if (downloadParams) {
      queryFilters = { ...queryFilters, ...downloadParams };
    }
    if (additionalParams) {
      queryFilters = {
        ...queryFilters,
        additional_params: JSON.stringify(additionalParams),
      };
    }
    return {
      ...queryFilters,
      visible_columns: visibleColumns,
      show_disabled_tags: showDisabledTags,
    };
  };

  const requestExport = additionalParams => {
    const queryFilters = buildQueryFilters(additionalParams);
    if (customTableSlug) {
      const { visible_columns, show_disabled_tags, ...rest } = queryFilters;
      const customFilters = visible_columns
        ? { ...rest, visible_columns }
        : rest;
      return TableExportService.exportCustomTable(
        customTableSlug,
        format,
        customFilters
      );
    }
    return TableExportService.exportTable(
      exportTableName,
      queryFilters,
      savedSearchName
    );
  };

  const { mutate: startExport, isPending } = useMutation({
    mutationFn: async additionalParams => {
      const resp = await requestExport(additionalParams);
      if (resp !== true) throw new Error("Export was not started");
    },
    onSuccess: () => {
      setOpen(false);
      const toastId = toast.success("Export started", {
        description: "You'll receive an email when it's ready.",
        action: (
          <Link
            to="/data"
            className={cn(
              buttonVariants({ variant: "link", size: "xs" }),
              "tw:ml-auto tw:h-auto tw:px-0 tw:font-semibold tw:text-inherit!"
            )}
            onClick={() => toast.dismiss(toastId)}
          >
            Go to Data
          </Link>
        ),
      });
    },
    // The dialog stays open on failure so the user can retry the same choice.
    onError: err =>
      toast.error(
        formatError(err, "The export could not be started. Please try again.")
      ),
  });

  const handleOpenChange = nextOpen => {
    if (!nextOpen && isPending) return;
    // The option can make the export much wider, so it is never carried over silently.
    if (nextOpen) setIsOptionChecked(false);
    setOpen(nextOpen);
  };

  const handleConfirm = () =>
    startExport(isOptionChecked ? exportOption.params : {});

  const exportTooltip = trigger => (
    <Tooltip>
      <TooltipTrigger render={trigger} />
      <TooltipContent>Export table</TooltipContent>
    </Tooltip>
  );

  if (!hasChoices) {
    return exportTooltip(
      <Button
        variant="outline"
        size="icon"
        aria-label="Export table"
        aria-busy={isPending}
        disabled={isDisabled || isPending}
        onClick={() => startExport({})}
      >
        {isPending ? <Spinner /> : <Download />}
      </Button>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {exportTooltip(
        <DialogTrigger
          aria-label="Export table"
          disabled={isDisabled}
          render={
            <Button variant="outline" size="icon">
              <Download />
            </Button>
          }
        />
      )}
      <DialogContent showCloseButton={!isPending}>
        <DialogHeader>
          <DialogTitle>Export table</DialogTitle>
          <DialogDescription>
            The export runs in the background. You&apos;ll receive an email when
            it&apos;s ready.
          </DialogDescription>
        </DialogHeader>
        {customTableSlug && (
          <Field>
            <FieldLabel htmlFor="export-format">Export format</FieldLabel>
            <Select value={format} onValueChange={setFormat}>
              <SelectTrigger id="export-format" className="tw:w-full">
                <SelectValue>
                  {value =>
                    FORMAT_OPTIONS.find(option => option.value === value)?.text
                  }
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {FORMAT_OPTIONS.map(option => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.text}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        )}
        {exportOption && (
          <Field orientation="horizontal">
            <Checkbox
              id="export-option"
              checked={isOptionChecked}
              onCheckedChange={setIsOptionChecked}
            />
            <FieldContent>
              <FieldLabel htmlFor="export-option" className="tw:font-normal">
                {exportOption.label}
              </FieldLabel>
              <FieldDescription>{exportOption.description}</FieldDescription>
            </FieldContent>
          </Field>
        )}
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => setOpen(false)}
            disabled={isPending}
          >
            Cancel
          </Button>
          <SaveButton
            saving={isPending}
            savingText="Exporting"
            onClick={handleConfirm}
          >
            Export
          </SaveButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
